import {
  WORLD_WIDTH, WORLD_HEIGHT,
  SHIP_RADIUS,
  BULLET_RADIUS, TICK_MS, ASTEROID_VERTEX_COUNT, WORLD_BORDER_LINE_WIDTH
} from 'asteroids-shared/constants'
import { wrappedDelta, asteroidVertices, toFiniteAsteroidId } from 'asteroids-shared/helpers'
import { MSG } from 'asteroids-shared/protocol'
import Camera from './renderer/Camera.js'
import ClientParticles from './ClientParticles.js'
import Keyboard from './controls/Keyboard.js'
import Gamepad from './controls/Gamepad.js'
import { drawShip, invulnerabilityAlpha } from './renderer/ShipRenderer.js'
import {
  drawShipIndicator,
  shipIndicatorEdgePadding,
  indicatorTargets,
  indicatorWorldVector,
  indicatorPointerRotationDeg,
  indicatorScreenEdgePoint
} from './renderer/ShipIndicatorRenderer.js'
import { drawAsteroid } from './renderer/AsteroidRenderer.js'
import { drawBullet } from './renderer/BulletRenderer.js'
import { drawParticle } from './renderer/ParticleRenderer.js'
import { drawBase, drawFlag } from './renderer/CtfRenderer.js'
import ShipSim from './prediction/ShipSim.js'
import SnapshotInterpolator from './net/SnapshotInterpolator.js'
import RenderClock from './net/RenderClock.js'
import Starfield from './renderer/Starfield.js'
import BulletSim from './prediction/BulletSim.js'
import {
  clampInterpLagSampleTime,
  drawDebugOverlayIfEnabled,
  HITBOX_OVERLAY_INTERP_LAG_MS,
  hitboxOverlayLagSampleMs,
  isDebugEnabled,
  localPlayerViewBulletDebug,
  updateHitboxOverlayLagEma
} from './debug/Debug.js'
import { collides, wrap } from 'asteroids-shared/physics'
import { deathmatchCountdownTotalSec } from './components/HUD.js'
import {
  computeShipOrientation,
  spawnRing
} from './ClientOrientation.js'

// Target interp delay (~ms behind newest serverTime; RenderClock steers playhead). `0` = no target lag.
export const RENDER_DELAY_MS = 60

/**
 * **Local prediction** = {@link ShipSim} + {@link BulletSim} on `this.prediction` (tick-sim, not snapshot rewind).
 * **Interp view** = {@link SnapshotInterpolator} at playhead `t` (~{@link RENDER_DELAY_MS} behind newest authority via {@link RenderClock}) for other ships, slugs, asteroids.
 * Asteroids render from the interp view with deterministic vertices ({@link asteroidVertices}) so clients match without shipping every vertex each tick.
 * Local slug spawn uses `bulletSpawnKinematicsFromShip` from shared `entities/Bullet.js`; on `spawn_bullet` pair, `serverId` / bullet `id` wire to the worker.
 * Interp playhead ({@link RenderClock}) advances by `simTicks * TICK_MS` after local sim, not raw wall `elapsed`.
 */
export default class ClientGame {
  #handlers = {
    resize: () => this.resize(),
    frame: () => this.frame(),
    spawnAsteroid: (m) => this.onSpawnAsteroid(m),
    spawnBullet: (m) => this.onSpawnBullet(m),
    destroyAsteroid: (m) => this.onDestroyAsteroid(m),
    destroyBullet: (m) => this.onDestroyBullet(m),
    shipHit: (m) => this.onShipHit(m),
    shipShielded: (m) => this.onShipShielded(m),
    shipRespawn: (m) => this.onShipRespawn(m),
    score: (m) => this.onScore(m),
    snapshot: (m) => this.onSnapshot(m),
    hudFeed: (m) => this.onHudFeed(m)
  }

  #interpDrawServerTime = 0
  #debug = {
    hitboxOverlayLagEmaMs: HITBOX_OVERLAY_INTERP_LAG_MS,
    predictionAsteroidOverlapPrev: false,
    predictionExplosionFxPlayed: false
  }
  #asteroid = {
    shapeCache: new Map(),
    unwrap: new Map()
  }
  #loop = {
    running: false,
    lastFrameMs: 0,
    simTickAccumulator: 0,
    simTickIndex: 0
  }
  #unsubs = []
  /** Last ceil-second bucket from frame-driven pushHud; null after a non-throttled pushHud */
  #hudTimerCeilSec = null
  /** playerId → performance.now() ms at which the shield-hit pulse expires. */
  #shieldFlashUntil = new Map()

  #shieldFlashFactor(playerId, nowMs) {
    const until = this.#shieldFlashUntil.get(playerId)
    if (!Number.isFinite(until) || until <= nowMs) {
      if (until != null) this.#shieldFlashUntil.delete(playerId)
      return 0
    }
    const remaining = until - nowMs
    return Math.max(0, Math.min(1, remaining / 220))
  }

  constructor({
    room, spawn, localPlayerId,
    canvas, socket, audio, hud
  }) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')
    this.socket = socket
    this.audio = audio
    this.localPlayerId = localPlayerId
    this.room = room
    this.toroidalMap = room.toroidalMap !== false
    this.worldBounds = { width: WORLD_WIDTH, height: WORLD_HEIGHT, toroidalMap: this.toroidalMap }
    this.#rebuildRosterById()


    this.spawn = {
      ...spawn,
      ...spawnRing(
        room.roster,
        spawn.playerOrder,
        localPlayerId,
        this.room.mode ?? 'coop'
      )
    }

    this.camera = new Camera({ worldWidth: WORLD_WIDTH, worldHeight: WORLD_HEIGHT, toroidalMap: this.toroidalMap })
    this.particles = new ClientParticles()
    this.starfield = new Starfield({ seedText: this.room.name })
    this.controls = {
      keys: { left: false, right: false, up: false, down: false, firePresses: 0 },
      keyboard: new Keyboard({ onActivity: () => this.audio.resume() }),
      gamepad: new Gamepad({ onActivity: () => this.audio.resume() }),
      enabled: true
    }

    this.interp = new SnapshotInterpolator({
      worldWidth: WORLD_WIDTH,
      worldHeight: WORLD_HEIGHT,
      toroidalMap: this.toroidalMap
    })
    const localMeta = this.room.rosterById.get(this.localPlayerId)
    if (!localMeta) {
      throw new Error(`Missing local player metadata for ${this.localPlayerId}`)
    }
    this.prediction = {
      shipSim: new ShipSim({
        shipId: localPlayerId,
        color: localMeta.color,
        name: localMeta.name,
        worldBounds: this.worldBounds
      })
    }
    this.prediction.bulletSim = new BulletSim({
      shipSim: this.prediction.shipSim,
      worldBounds: this.worldBounds,
      socket: this.socket,
      interp: this.interp,
      getClientSimTick: () => this.#loop.simTickIndex,
      audio: this.audio
    })

    this.renderClock = new RenderClock({ targetDelayMs: RENDER_DELAY_MS })
    this.modeState = null
    this.hud = { score: 0, lives: 3, teamScores: null, ...hud, feed: { list: [], ...hud.feed } }
    this.#syncSpawnOrientationFromMode()
  }

  #syncSpawnOrientationFromMode() {
    this.spawn.orientation = computeShipOrientation(
      this.room.mode ?? 'coop',
      this.spawn.ringIndex,
      this.spawn.ringSize
    )
  }

  #rebuildRosterById() {
    this.room.rosterById = new Map(
      this.room.roster.map(player => [
        player.id,
        {
          name: player.name,
          color: player.color,
          slot: player.slot,
          teamId: player.teamId ?? null,
          teamName: player.teamName ?? null
        }
      ])
    )
  }

  updateRoster(lobby) {
    if (lobby.hostId != null) this.room.hostId = lobby.hostId
    if (lobby.mode != null && lobby.mode !== '') this.room.mode = lobby.mode
    if (lobby.toroidalMap != null) this.room.toroidalMap = lobby.toroidalMap
    this.#syncSpawnOrientationFromMode()
    this.#rebuildRosterById()
    const local = this.room.rosterById.get(this.localPlayerId)
    if (local) {
      this.prediction.shipSim.ship.color = local.color
      this.prediction.shipSim.ship.name = local.name
      this.prediction.shipSim.ship.teamId = local.teamId ?? null
      this.prediction.shipSim.ship.teamName = local.teamName ?? null
    }
    this.pushHud()
  }

  // ---- lifecycle ------------------------------------------------------
  start() {
    this.#loop.running = true
    this.controls.keyboard.start()
    this.controls.gamepad.start()
    window.addEventListener('resize', this.#handlers.resize)
    this.resize()

    for (const [type, key] of [
      [MSG.SPAWN_ASTEROID, 'spawnAsteroid'],
      [MSG.SPAWN_BULLET, 'spawnBullet'],
      [MSG.DESTROY_ASTEROID, 'destroyAsteroid'],
      [MSG.DESTROY_BULLET, 'destroyBullet'],
      [MSG.SHIP_HIT, 'shipHit'],
      [MSG.SHIP_SHIELDED, 'shipShielded'],
      [MSG.SHIP_RESPAWN, 'shipRespawn'],
      [MSG.SCORE_UPDATE, 'score'],
      [MSG.SNAPSHOT, 'snapshot'],
      [MSG.HUD_FEED, 'hudFeed']
    ]) {
      this.#unsubs.push(this.socket.on(type, this.#handlers[key]))
    }

    this.socket.drainReplay?.()

    this.#loop.lastFrameMs = performance.now()
    requestAnimationFrame(this.#handlers.frame)
    this.pushHud()
  }

  stop() {
    this.#loop.running = false
    this.controls.keyboard.stop()
    this.controls.gamepad.stop()
    this.audio?.setThruster(false)
    window.removeEventListener('resize', this.#handlers.resize)
    for (const u of this.#unsubs) u()
    this.#unsubs = []
    this.#loop.simTickIndex = 0
  }

  // ---- input / canvas -------------------------------------------------
  setInputEnabled(enabled) {
    this.controls.enabled = !!enabled
    if (!this.controls.enabled) {
      this.controls.keys.left = false
      this.controls.keys.right = false
      this.controls.keys.up = false
      this.controls.keys.down = false
      this.controls.keys.firePresses = 0
      this.audio?.setThruster(false)
    }
  }

  pollInput() {
    if (!this.controls.enabled) return
    const k = this.controls.keyboard.poll()
    const g = this.controls.gamepad.poll()
    this.controls.keys.left = k.left || g.left
    this.controls.keys.right = k.right || g.right
    this.controls.keys.up = k.up || g.up
    this.controls.keys.down = k.down || g.down
    this.controls.keys.firePresses = (k.firePresses | 0) + (g.firePresses | 0)
  }

  resize() {
    const ratio = window.devicePixelRatio || 1
    this.canvas.width = window.innerWidth * ratio
    this.canvas.height = window.innerHeight * ratio
    this.canvas.style.width = `${window.innerWidth}px`
    this.canvas.style.height = `${window.innerHeight}px`
    this.camera.resize(window.innerWidth, window.innerHeight)
    this.dpr = ratio
  }

  // ---- net handlers (world events) -----------------------------------
  onSpawnAsteroid(m) {
    this.#getAsteroidVertices(m.id, m.radius)
  }

  onDestroyAsteroid(m) {
    const astId = toFiniteAsteroidId(m.id)
    if (astId == null) return
    const cached = this.#asteroid.shapeCache.get(astId)
    this.#asteroid.unwrap.delete(astId)
    const r = m.radius ?? cached?.radius ?? 20
    const distance = this.distanceToLocalShip(m.x, m.y)
    this.audio.playAsteroidExplosion({ radius: r, distance })
    this.particles.spawnAsteroidBurst(m.x, m.y, r)
    this.#asteroid.shapeCache.delete(astId)
    if (m.children) {
      for (const c of m.children) {
        this.#getAsteroidVertices(c.id, c.radius)
      }
    }
  }

  onSpawnBullet(m) {
    this.prediction.bulletSim.onServerSpawnBullet(m)
  }

  onDestroyBullet(m) {
    this.interp.noteBulletDestroyed(m.id)
    this.prediction.bulletSim.onServerDestroyBullet(m)
  }

  onShipHit(m) {
    const localId = this.prediction.shipSim.ship.id
    const skipLocalExplosionFx = m.playerId === localId && this.#debug.predictionExplosionFxPlayed
    if (!skipLocalExplosionFx) {
      this.particles.spawnShipExplosion(m.x, m.y)
    }
    if (m.playerId === localId) {
      this.#debug.predictionExplosionFxPlayed = false
      this.#debug.predictionAsteroidOverlapPrev = false
      this.audio.setThruster(false)
      this.audio.playDeathThunder()
      this.prediction.shipSim.onDied()
      this.prediction.bulletSim.resetOnLocalDeath()
    }
  }

  onShipShielded(m) {
    const localId = this.prediction.shipSim.ship.id
    this.particles.spawnShieldFlash(m.x, m.y)
    this.audio.playShieldHit?.()
    this.#shieldFlashUntil.set(m.playerId, performance.now() + 220)
    if (m.playerId === localId) {
      const ship = this.prediction.shipSim.ship
      ship.shieldHealth = Math.max(0, m.shieldHealth ?? Math.max(0, ship.shieldHealth - 1))
      if (Number.isFinite(m.shieldMax)) ship.shieldMax = m.shieldMax
      this.pushHud()
    }
  }

  onShipRespawn(m) {
    if (m.playerId === this.prediction.shipSim.ship.id) {
      /**
       * Next snapshot carries fresh authority; until then, snap prediction ship
       * to spawn so the gap is not stuck at the old pose.
       */
      this.prediction.shipSim.ship.setState({
        x: m.x,
        y: m.y,
        vx: 0,
        vy: 0,
        rotation: Number.isFinite(m.rotation) ? m.rotation : 0
      })
      this.prediction.shipSim.ship.alive = true
      this.prediction.shipSim.history = []
      this.prediction.bulletSim.resetOnLocalDeath()
      this.#debug.predictionAsteroidOverlapPrev = false
      this.#debug.predictionExplosionFxPlayed = false
      this.prediction.shipSim.ship.invulnerableUntil = m.invulnerableUntil ?? 0
    }
  }

  onScore(m) {
    if (m.mode != null && m.mode !== '') {
      this.room.mode = m.mode
      this.#syncSpawnOrientationFromMode()
    }
    if (this.room.mode === 'deathmatch' || this.room.mode === 'ctf') {
      const row = m.scoreboard?.find(
        e => String(e.playerId) === String(this.localPlayerId)
      )
      if (row && Number.isFinite(row.score)) this.hud.score = row.score
      this.hud.teamScores = this.room.mode === 'ctf'
        ? (m.teamScores ?? this.hud.teamScores)
        : null
    } else {
      if (Number.isFinite(m.score)) this.hud.score = m.score
      if (Number.isFinite(m.lives)) this.hud.lives = m.lives
      this.hud.teamScores = null
    }
    this.pushHud()
  }

  onSnapshot(m) {
    this.interp.push({
      serverTime: m.serverTime,
      ships: m.ships,
      asteroids: m.asteroids,
      bullets: m.bullets,
      modeState: m.modeState ?? null
    })
    this.modeState = m.modeState ?? null
    if (!this.renderClock.initialized() && Number.isFinite(m.serverTime)) {
      this.renderClock.advance(0, m.serverTime)
    }
    // Reconcile only the local ship; other ships are rendered from interp.
    const me = m.ships.find(s => s.id === this.prediction.shipSim.ship.id)
    if (me) this.prediction.shipSim.reconcile(me)
  }

  // ---- hud ------------------------------------------------------------
  pushHud(opts = {}) {
    const throttleTimer = opts.throttleTimer === true
    const roundRemainingMs = Number.isFinite(this.room.round?.end)
      ? Math.max(0, this.room.round.end - this.socket.serverNow())
      : null
    this.#pruneFeedList()
    if (throttleTimer && roundRemainingMs != null) {
      const totalSec = deathmatchCountdownTotalSec(roundRemainingMs)
      if (totalSec != null && totalSec === this.#hudTimerCeilSec) return
      if (totalSec != null) this.#hudTimerCeilSec = totalSec
    } else if (!throttleTimer) {
      this.#hudTimerCeilSec = null
    }
    const localShip = this.prediction.shipSim.ship
    this.hud.onChange?.({
      score: this.hud.score,
      lives: Math.max(0, this.hud.lives),
      teamScores: this.hud.teamScores,
      roundRemainingMs,
      mode: this.room.mode ?? 'coop',
      shieldHealth: localShip?.shieldHealth ?? 0,
      shieldMax: localShip?.shieldMax ?? 0,
      shieldColor: localShip?.color ?? '#fff'
    })
  }

  onHudFeed(message) {
    let text
    if (message.method === 'ram' && message.playerA != null && message.playerB != null) {
      text = `${this.#playerLabelForFeed(message.playerA)} ↔ ${this.#playerLabelForFeed(message.playerB)}`
    } else if (message.method === 'flag_pickup' && message.playerId != null) {
      text = `${this.#playerLabelForFeed(message.playerId)} took the flag`
    } else if (message.method === 'flag_drop' && message.playerId != null) {
      text = `${this.#playerLabelForFeed(message.playerId)} dropped the flag`
    } else if (message.method === 'flag_capture' && message.playerId != null) {
      const teamLabel = message.scoringTeamId === 'yellow'
        ? 'Yellow'
        : message.scoringTeamId === 'cyan'
          ? 'Cyan'
          : null
      text = message.ownGoal && teamLabel
        ? `${this.#playerLabelForFeed(message.playerId)} scored for ${teamLabel}`
        : `${this.#playerLabelForFeed(message.playerId)} captured the flag`
      this.audio?.playCtfScoreFanfare()
    } else {
      text = `${this.#playerLabelForFeed(message.killerId)} → ${this.#playerLabelForFeed(message.victimId)}`
    }
    this.hud.feed.list.push({
      id: `${Date.now()}-${Math.random()}`,
      text,
      expiresAt: Date.now() + 6000
    })
    this.#pruneFeedList()
    setTimeout(() => this.#pruneFeedList(), 6100)
  }

  #playerLabelForFeed(playerId) {
    return this.room.rosterById.get(playerId)?.name ?? String(playerId)
  }

  #pruneFeedList() {
    const now = Date.now()
    this.hud.feed.list = this.hud.feed.list.filter(item => item.expiresAt > now)
    this.hud.feed.onChange?.()
  }

  // ---- frame loop -----------------------------------------------------
  frame() {
    if (!this.#loop.running) return
    const now = performance.now()
    const elapsed = Math.min(now - this.#loop.lastFrameMs, 100)
    this.#loop.lastFrameMs = now

    // Particles advance in tick-units too so physics feel matches server.
    this.particles.update(elapsed / TICK_MS)
    this.prediction.shipSim.decayRenderError(elapsed)
    const latestServerTime = this.interp.latest()?.serverTime ?? null

    this.#loop.simTickAccumulator += elapsed
    let simTicks = 0
    while (this.#loop.simTickAccumulator >= TICK_MS) {
      this.simTick()
      this.#loop.simTickAccumulator -= TICK_MS
      simTicks++
    }
    const simAdvanceMs = simTicks * TICK_MS
    this.renderClock.advance(
      simAdvanceMs,
      Number.isFinite(latestServerTime) ? latestServerTime : null
    )

    this.#interpDrawServerTime = this.renderClock.interpSampleTime(latestServerTime, RENDER_DELAY_MS)

    if (isDebugEnabled()) {
      const sample = hitboxOverlayLagSampleMs(this.socket.serverNow(), latestServerTime)
      this.#debug.hitboxOverlayLagEmaMs = updateHitboxOverlayLagEma(this.#debug.hitboxOverlayLagEmaMs, sample, elapsed)
    } else {
      this.#debug.hitboxOverlayLagEmaMs = HITBOX_OVERLAY_INTERP_LAG_MS
    }

    const tInterpDraw = Number.isFinite(this.#interpDrawServerTime) ? this.#interpDrawServerTime : NaN
    this.interp.commitView(tInterpDraw)

    this.draw()
    if (Number.isFinite(this.room.round?.end)) {
      this.pushHud({ throttleTimer: true })
    }
    requestAnimationFrame(this.#handlers.frame)
  }

  // ---- local sim tick -------------------------------------------------
  simTick() {
    this.pollInput()
    if (this.socket.serverNow() < this.prediction.shipSim.ship.invulnerableUntil) {
      this.controls.keys.firePresses = 0
    }
    const { seq, keys, wallBounce } = this.prediction.shipSim.tick(this.controls.keys)
    this.socket.sendInput(seq, keys)
    if (wallBounce && !this.toroidalMap) {
      void this.audio.resume()
      this.audio.playWallBoing()
    }
    this.prediction.bulletSim.maybeSpawnLocalBullet(keys, seq)
    this.prediction.bulletSim.updateLocalBullets()
    // Thruster particles follow the prediction ship when we applied `up`.
    if (keys.up && this.prediction.shipSim.ship.alive) {
      this.particles.spawnThruster(this.prediction.shipSim.ship)
    }
    this.audio.setThruster(this.prediction.shipSim.ship.alive && this.prediction.shipSim.ship.thrustingThisTick)
    this.#loop.simTickIndex++
  }

  // ---- helpers --------------------------------------------------------
  distanceToLocalShip(x, y) {
    if (!this.prediction.shipSim.initialized) return 0
    const ship = this.prediction.shipSim.ship
    const dx = this.toroidalMap
      ? wrappedDelta(x, ship.position.x, WORLD_WIDTH)
      : x - ship.position.x
    const dy = this.toroidalMap
      ? wrappedDelta(y, ship.position.y, WORLD_HEIGHT)
      : y - ship.position.y
    return Math.sqrt(dx * dx + dy * dy)
  }

  #flagAnchor(flag, viewShips, renderedPose) {
    if (!flag) return null
    if (flag.carrierId != null) {
      if (
        String(flag.carrierId) === String(this.prediction.shipSim.ship.id)
        && this.prediction.shipSim.initialized
        && this.prediction.shipSim.ship.alive
      ) {
        return { x: renderedPose.x, y: renderedPose.y - 24 }
      }
      const carrier = viewShips.find(ship => String(ship.id) === String(flag.carrierId) && ship.alive)
      if (carrier) return { x: carrier.x, y: carrier.y - 24 }
    }
    if (!Number.isFinite(flag.x) || !Number.isFinite(flag.y)) return null
    return { x: flag.x, y: flag.y }
  }

  #unwrapAlongTorus(prevU, canon, span) {
    const prevC = ((prevU % span) + span) % span
    let d = canon - prevC
    if (d > span / 2) d -= span
    else if (d < -span / 2) d += span
    return prevU + d
  }

  #anchorNearRef(u, ref, span) {
    if (!Number.isFinite(u) || !Number.isFinite(ref)) return u
    let out = u
    while (out - ref > span / 2) out -= span
    while (out - ref < -span / 2) out += span
    return out
  }

  #updateAsteroidUnwrap(a) {
    if (!this.toroidalMap) return { x: a.x, y: a.y }
    const id = toFiniteAsteroidId(a.id)
    if (id == null) return { x: a.x, y: a.y }
    let u = this.#asteroid.unwrap.get(id)
    if (!u) {
      u = { x: a.x, y: a.y }
      this.#asteroid.unwrap.set(id, u)
      return u
    }
    u.x = this.#unwrapAlongTorus(u.x, a.x, WORLD_WIDTH)
    u.y = this.#unwrapAlongTorus(u.y, a.y, WORLD_HEIGHT)
    const ref = this.prediction.shipSim.initialized
      ? this.prediction.shipSim.renderedPose()
      : { x: a.x, y: a.y }
    u.x = this.#anchorNearRef(u.x, ref.x, WORLD_WIDTH)
    u.y = this.#anchorNearRef(u.y, ref.y, WORLD_HEIGHT)
    return u
  }

  #getAsteroidVertices(rawId, radius) {
    const id = toFiniteAsteroidId(rawId)
    if (id == null || !Number.isFinite(radius)) return null
    const cached = this.#asteroid.shapeCache.get(id)
    if (cached?.radius === radius) return cached.vertices
    const vertices = asteroidVertices(id, ASTEROID_VERTEX_COUNT, radius)
    this.#asteroid.shapeCache.set(id, { radius, vertices })
    return vertices
  }

  // ---- debug (hitbox overlay) ----------------------------------------
  // Hitbox overlay only: prediction ship vs interp asteroids (no pad); edge-trigger explosion FX.
  #stepPredictionAsteroidOverlap() {
    if (!isDebugEnabled()) return
    const predictionShip = this.prediction.shipSim.ship
    if (!this.prediction.shipSim.initialized || !predictionShip.alive) {
      this.#debug.predictionAsteroidOverlapPrev = false
      return
    }
    const bounds = this.worldBounds
    let overlapping = false
    for (const a of this.interp.view.asteroids) {
      const r = a.radius
      if (!Number.isFinite(r)) continue
      const ast = { position: { x: a.x, y: a.y }, radius: r }
      if (collides(predictionShip, ast, bounds)) {
        overlapping = true
        break
      }
    }
    if (overlapping && !this.#debug.predictionAsteroidOverlapPrev) {
      const renderedPose = this.prediction.shipSim.renderedPose()
      this.particles.spawnShipExplosion(renderedPose.x, renderedPose.y)
      this.#debug.predictionExplosionFxPlayed = true
    }
    this.#debug.predictionAsteroidOverlapPrev = overlapping
  }

  drawWorldVoid(ctx) {
    if (this.toroidalMap) return
    const left = -this.camera.x
    const top = -this.camera.y
    const right = left + WORLD_WIDTH
    const bottom = top + WORLD_HEIGHT
    ctx.fillStyle = '#000'

    if (left > 0) ctx.fillRect(0, 0, left, this.camera.viewH)
    if (right < this.camera.viewW) ctx.fillRect(right, 0, this.camera.viewW - right, this.camera.viewH)
    if (top > 0) ctx.fillRect(0, 0, this.camera.viewW, top)
    if (bottom < this.camera.viewH) ctx.fillRect(0, bottom, this.camera.viewW, this.camera.viewH - bottom)
  }

  drawWorldBorder(ctx) {
    if (this.toroidalMap) return
    ctx.save()
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = WORLD_BORDER_LINE_WIDTH
    ctx.strokeRect(-this.camera.x, -this.camera.y, WORLD_WIDTH, WORLD_HEIGHT)
    ctx.restore()
  }

  // ---- draw -----------------------------------------------------------
  draw() {
    const view = this.interp.view
    const modeState = view.modeState ?? this.modeState

    for (const a of view.asteroids) {
      this.#updateAsteroidUnwrap(a)
    }
    const liveAstIds = new Set(
      view.asteroids.map((a) => toFiniteAsteroidId(a.id)).filter((n) => n != null)
    )
    for (const id of this.#asteroid.unwrap.keys()) {
      if (!liveAstIds.has(id)) this.#asteroid.unwrap.delete(id)
    }

    const predictionShip = this.prediction.shipSim.ship
    const renderedPose = this.prediction.shipSim.renderedPose()
    if (this.prediction.shipSim.initialized) {
      this.camera.follow({ position: { x: renderedPose.x, y: renderedPose.y } })
    }

    let asteroidsInterpOverlayLag = null
    let shipsInterpOverlayLag = null
    // When hitbox overlay is on, interp bullets use the same lagged sample as overlay rings (not raw view.bullets at the main interp playhead).
    let bulletsInterpDraw = view.bullets
    if (isDebugEnabled() && Number.isFinite(this.#interpDrawServerTime)) {
      const lagMs = this.#debug.hitboxOverlayLagEmaMs
      const tDraw = this.#interpDrawServerTime
      const tLag = clampInterpLagSampleTime(this.interp, tDraw, lagMs)
      const interpLagView = this.interp.sample(tLag)
      asteroidsInterpOverlayLag = interpLagView.asteroids
      shipsInterpOverlayLag = interpLagView.ships
      const merged = [...interpLagView.bullets]
      const seen = new Set(merged.map((b) => b.id))
      for (const e of this.prediction.bulletSim.localBullets) {
        if (e.serverId == null || !Number.isFinite(e.serverId)) continue
        if (seen.has(e.serverId)) continue
        const anchor = this.prediction.bulletSim.spawnAnchors.get(e.serverId)
        if (!anchor || !Number.isFinite(anchor.serverTime)) continue
        const p = this.interp.bulletPositionAt(anchor, anchor.serverTime, tLag)
        merged.push({
          id: e.serverId,
          x: p.x,
          y: p.y,
          ownerId: this.prediction.shipSim.ship.id
        })
        seen.add(e.serverId)
      }
      const localShipId = this.prediction.shipSim.ship.id
      for (let i = 0; i < merged.length; i++) {
        const b = merged[i]
        if (String(b.ownerId) !== String(localShipId)) continue
        const anchor = this.prediction.bulletSim.spawnAnchors.get(b.id)
        const o = anchor?.redSlugSpawnOffset
        if (!o || (o.x === 0 && o.y === 0)) continue
        const p = { x: b.x + o.x, y: b.y + o.y }
        if (this.toroidalMap) wrap(p, WORLD_WIDTH, WORLD_HEIGHT, BULLET_RADIUS)
        merged[i] = { ...b, x: p.x, y: p.y }
      }
      bulletsInterpDraw = merged
    }

    const vw = this.camera.viewW
    const vh = this.camera.viewH
    const ctx = this.ctx
    ctx.save()
    ctx.scale(this.dpr, this.dpr)

    ctx.fillStyle = '#000'
    ctx.globalAlpha = 0.4
    ctx.fillRect(0, 0, vw, vh)
    ctx.globalAlpha = 1

    ctx.save()
    if (Math.abs(this.spawn.orientation) > 1e-5) {
      ctx.translate(vw / 2, vh / 2)
      ctx.rotate(this.spawn.orientation)
      ctx.translate(-vw / 2, -vh / 2)
    }
    this.starfield.draw(ctx, this.camera)
    this.drawWorldVoid(ctx)
    this.drawWorldBorder(ctx)

    if (modeState?.kind === 'ctf') {
      for (const base of modeState.bases ?? []) {
        drawBase(ctx, base, this.camera)
      }
    }

    for (const a of view.asteroids) {
      const radius = a.radius
      if (!Number.isFinite(radius)) continue
      const vertices = this.#getAsteroidVertices(a.id, radius)
      if (!Array.isArray(vertices) || vertices.length < 3) continue

      const asteroidId = toFiniteAsteroidId(a.id)
      const u = asteroidId != null && this.#asteroid.unwrap.has(asteroidId)
        ? this.#asteroid.unwrap.get(asteroidId)
        : { x: a.x, y: a.y }
      if (!u || !Number.isFinite(u.x) || !Number.isFinite(u.y)) continue
      if (!Number.isFinite(a.x) || !Number.isFinite(a.y)) continue

      const rot = a.rotation ?? a.rot ?? 0
      const drawnCopies = drawAsteroid(ctx, {
        position: { x: u.x, y: u.y },
        rotation: rot,
        radius,
        vertices
      }, this.camera, { dim: isDebugEnabled() })
      if (drawnCopies === 0) continue
    }

    for (const entry of this.prediction.bulletSim.localBullets) {
      const pos = this.prediction.bulletSim.bulletDrawPosition(entry)
      drawBullet(ctx, { position: pos, radius: BULLET_RADIUS, color: '#fff' }, this.camera)
    }

    for (const b of bulletsInterpDraw) {
      if (localPlayerViewBulletDebug(ctx, this.camera, b, {
        shipId: this.prediction.shipSim.ship.id
      })) continue
      drawBullet(ctx, { position: { x: b.x, y: b.y }, radius: BULLET_RADIUS }, this.camera)
    }

    /**
     * Remote ships from interp. Skip local ship (prediction draw below for
     * responsiveness).
     */
    const serverNow = this.socket.serverNow()
    const nowMs = performance.now()
    for (const s of view.ships) {
      if (s.id === this.prediction.shipSim.ship.id) continue
      if (!s.alive) continue
      drawShip(ctx, {
        position: { x: s.x, y: s.y },
        rotation: s.rotation,
        radius: SHIP_RADIUS,
        alive: true,
        color: s.color, name: s.name,
        shieldHealth: s.shieldHealth ?? 0,
        shieldFlash: this.#shieldFlashFactor(s.id, nowMs),
        globalAlpha: invulnerabilityAlpha(serverNow, s.invulnerableUntil)
      }, this.camera)
    }

    if (predictionShip.alive && this.prediction.shipSim.initialized) {
      drawShip(ctx, {
        position: { x: renderedPose.x, y: renderedPose.y },
        rotation: renderedPose.rotation,
        radius: predictionShip.radius,
        alive: true,
        color: predictionShip.color,
        name: predictionShip.name,
        thrustingThisTick: predictionShip.thrustingThisTick,
        thrusting: predictionShip.thrusting,
        shieldHealth: predictionShip.shieldHealth ?? 0,
        shieldFlash: this.#shieldFlashFactor(predictionShip.id, nowMs),
        globalAlpha: invulnerabilityAlpha(serverNow, predictionShip.invulnerableUntil)
      }, this.camera)
    }

    if (modeState?.kind === 'ctf') {
      const flagAnchor = this.#flagAnchor(modeState.flag, view.ships, renderedPose)
      if (flagAnchor) drawFlag(ctx, flagAnchor, this.camera)
    }

    if (this.prediction.shipSim.initialized && predictionShip.alive) {
      const edgePad = shipIndicatorEdgePadding()
      if (edgePad * 2 < vw && edgePad * 2 < vh) {
        for (const s of indicatorTargets(view, this.prediction.shipSim.ship.id)) {
          if (this.camera.anyVisibleCopy(s.x, s.y, SHIP_RADIUS)) continue

          const ship = this.prediction.shipSim.ship
          const { dx, dy } = indicatorWorldVector(ship.position.x, ship.position.y, s.x, s.y, this.toroidalMap)
          const len = Math.hypot(dx, dy)
          if (len < 1e-6) continue

          const udx = dx / len
          const udy = dy / len
          const edge = indicatorScreenEdgePoint(udx, udy, edgePad, vw, vh)
          if (!edge) continue

          drawShipIndicator(ctx, {
            screenX: edge.x,
            screenY: edge.y,
            angleDeg: indicatorPointerRotationDeg(dx, dy),
            color: s.color,
            camera: this.camera
          })
        }
      }
    }

    drawDebugOverlayIfEnabled(ctx, {
      view,
      asteroidsInterpOverlayLag,
      shipsInterpOverlayLag,
      asteroidMeta: this.#asteroid.shapeCache,
      camera: this.camera,
      asteroidUnwrap: this.#asteroid.unwrap,
      asteroidsClient: [],
      localShipHitboxClient: this.prediction.shipSim.initialized && predictionShip.alive
        ? { x: renderedPose.x, y: renderedPose.y }
        : null
    })

    this.#stepPredictionAsteroidOverlap()

    for (const p of this.particles.list) drawParticle(ctx, p, this.camera)

    ctx.restore()
    ctx.restore()
  }
}
